import { useAuthStore, useIsAdmin, useAffiliatedSiteIds } from '../../lib/auth';
import { useQuery } from '@tanstack/react-query';
import { siteGetSites } from '../../api';
import { SmartFieldClaimType } from '../../lib/auth/types';
import { Link } from 'react-router-dom';

export function ManageAccountPage() {
  const user = useAuthStore(state => state.user);
  const isAdmin = useIsAdmin();
  const affiliatedSiteIds = useAffiliatedSiteIds();

  const { data: sites } = useQuery({
    queryKey: ['sites'],
    queryFn: () => siteGetSites(),
    enabled: isAdmin || affiliatedSiteIds.length > 0,
  });

  if (!user) {
    return null;
  }

  // Filter sites to only show affiliated sites (or all if admin)
  const userSites = isAdmin 
    ? sites 
    : sites?.filter(site => site.id && affiliatedSiteIds.includes(site.id));

  // Helper to get site name by ID
  const getSiteName = (siteId: number): string => {
    return userSites?.find(s => s.id === siteId)?.name || `Site ${siteId}`;
  };

  // Group permissions by site
  const sitePermissionsMap = new Map<number, Set<SmartFieldClaimType>>();
  
  for (const [claimType, siteIds] of user.sitePermissions.entries()) {
    for (const siteId of siteIds) {
      if (!sitePermissionsMap.has(siteId)) {
        sitePermissionsMap.set(siteId, new Set());
      }
      sitePermissionsMap.get(siteId)!.add(claimType);
    }
  }

  // Get permission label
  const getPermissionLabel = (claimType: SmartFieldClaimType): string => {
    const labels: Record<SmartFieldClaimType, string> = {
      [SmartFieldClaimType.None]: 'None',
      [SmartFieldClaimType.SiteManager]: 'Site Manager',
      [SmartFieldClaimType.UserManager]: 'User Manager',
      [SmartFieldClaimType.RawDataExporter]: 'Raw Data Exporter',
      [SmartFieldClaimType.RawDataReader]: 'Raw Data Reader',
      [SmartFieldClaimType.RawDataEditor]: 'Raw Data Editor',
      [SmartFieldClaimType.FluxDataExporter]: 'Flux Data Exporter',
      [SmartFieldClaimType.FluxDataReader]: 'Flux Data Reader',
      [SmartFieldClaimType.FluxDataEditor]: 'Flux Data Editor',
      [SmartFieldClaimType.TrialManager]: 'Trial Manager',
      [SmartFieldClaimType.ApiKey]: 'API Key',
      [SmartFieldClaimType.Tenant]: 'Tenant',
    };
    return labels[claimType] || claimType;
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Account Settings</h1>
        <p className="mt-2 text-gray-600">Manage your profile and view your permissions</p>
      </div>

      {/* Profile Information */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Profile Information</h2>
          <div className="flex gap-3">
            <Link
              to="/account/change-email"
              className="px-4 py-2 text-sm font-medium text-smartfield border border-smartfield rounded-md hover:bg-smartfield hover:text-white transition-colors"
            >
              Change Email
            </Link>
            <Link
              to="/account/change-password"
              className="px-4 py-2 text-sm font-medium text-white bg-smartfield rounded-md hover:bg-smartfield/90 transition-colors"
            >
              Change Password
            </Link>
          </div>
        </div>
        <div className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <p className="mt-1 text-gray-900">{user.email}</p>
            <div className="mt-1">
              {user.isEmailConfirmed ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Confirmed
                </span>
              ) : (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  Pending Confirmation
                </span>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Roles</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {user.roles.length > 0 ? (
                user.roles.map(role => (
                  <span
                    key={role}
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                      role === 'Admin' 
                        ? 'bg-purple-100 text-purple-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}
                  >
                    {role}
                  </span>
                ))
              ) : (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                  User
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Site Permissions */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Site Permissions</h2>
          <p className="mt-1 text-sm text-gray-600">
            {isAdmin 
              ? 'As an admin, you have access to all sites and features.' 
              : 'Your permissions for each affiliated site.'}
          </p>
        </div>
        <div className="px-6 py-4">
          {isAdmin ? (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="font-medium text-purple-900">Administrator Access</span>
              </div>
              <p className="mt-2 text-sm text-purple-800">
                You have full access to all sites, users, and system features.
              </p>
            </div>
          ) : sitePermissionsMap.size === 0 ? (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <h3 className="mt-4 text-sm font-medium text-gray-900">No Site Permissions</h3>
              <p className="mt-2 text-sm text-gray-500">
                You don't have permissions assigned to any sites yet. Contact an administrator to grant you access.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {Array.from(sitePermissionsMap.entries())
                .sort(([a], [b]) => getSiteName(a).localeCompare(getSiteName(b)))
                .map(([siteId, permissions]) => (
                  <div key={siteId} className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-medium text-gray-900 mb-3">{getSiteName(siteId)}</h3>
                    <div className="flex flex-wrap gap-2">
                      {Array.from(permissions)
                        .sort((a, b) => getPermissionLabel(a).localeCompare(getPermissionLabel(b)))
                        .map(permission => (
                          <span
                            key={permission}
                            className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-smartfield-light text-smartfield border border-smartfield"
                          >
                            {getPermissionLabel(permission)}
                          </span>
                        ))}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>

      {/* Affiliated Sites List */}
      {!isAdmin && userSites && userSites.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Affiliated Sites</h2>
            <p className="mt-1 text-sm text-gray-600">Sites you have access to</p>
          </div>
          <div className="px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {userSites.map(site => (
                <div
                  key={site.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-smartfield transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium text-gray-900">{site.name}</h3>
                      {site.serialNumber && (
                        <p className="text-sm text-gray-500 mt-1">S/N: {site.serialNumber}</p>
                      )}
                    </div>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      site.stoppedAtUtc ? 'bg-gray-100 text-gray-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {site.stoppedAtUtc ? 'Stopped' : 'Active'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* All Claims (Debug Info - Optional) */}
      {isAdmin && (
        <details className="bg-gray-50 rounded-lg border border-gray-200">
          <summary className="px-6 py-4 cursor-pointer font-medium text-gray-900 hover:bg-gray-100 rounded-t-lg">
            Advanced: All Claims (Debug)
          </summary>
          <div className="px-6 py-4 border-t border-gray-200 max-h-96 overflow-auto">
            <pre className="text-xs text-gray-700 whitespace-pre-wrap break-all">
              {JSON.stringify(
                Array.from(user.claims.entries()).reduce((obj, [key, values]) => {
                  obj[key] = values;
                  return obj;
                }, {} as Record<string, string[]>),
                null,
                2
              )}
            </pre>
          </div>
        </details>
      )}
    </div>
  );
}
